import { cacheCatalogedModel, versionedAssetUrl } from '@sorrem/engine';
import { createLogger, type AssetManifest, type AssetManifestEntry, type AssetManifestModel, type AssetPrecacheTier } from '@sorrem/shared';

const logger = createLogger('client:asset-precache');

/** Files fetched at once while the player waits to enter: Chrome's whole per-host connection allowance. */
const ENTRY_PRECACHE_CONCURRENCY = 6;

/**
 * Files fetched at once in the background (#355): few enough that the
 * screen's own requests (the API, assets it needs now) always find a free
 * connection to the same host.
 */
const BACKGROUND_PRECACHE_CONCURRENCY = 2;

/** Where precaching is: not started, caching the core tier (entry waits for it), the run tier (in the background), or done. */
export type AssetPrecachePhase = 'idle' | 'core' | 'run' | 'done';

export interface AssetPrecacheState {
    readonly phase: AssetPrecachePhase;
    /** Core tier files fetched so far, and how many it has; 0 in every other phase, which nobody is shown. */
    readonly done: number;
    readonly total: number;
}

/** How each kind of file is fetched into the client's caches. */
export interface AssetPrecacheFetchers {
    /** A catalog model, into the model cache (IndexedDB). */
    model(model: AssetManifestModel): Promise<unknown>;
    /** Any other file, into the HTTP cache by its versioned URL. */
    file(path: string): Promise<unknown>;
}

/** A tier's files, smallest first, so the count moves quickly and the big models come last. */
export function selectPrecacheEntries(manifest: AssetManifest, tier: AssetPrecacheTier): AssetManifestEntry[] {
    return manifest.entries.filter((entry) => entry.tier === tier).sort((a, b) => a.sizeBytes - b.sizeBytes);
}

/**
 * Fetches each entry into the client's caches, a few at a time, telling
 * `onProgress` (if given) after each. A file that fails is logged and
 * skipped: it is fetched again when first used.
 */
export async function precacheEntries(
    entries: readonly AssetManifestEntry[],
    fetchers: AssetPrecacheFetchers,
    onProgress: ((done: number) => void) | null,
    concurrency: number,
): Promise<void> {
    let next = 0;
    let done = 0;
    const worker = async (): Promise<void> => {
        while (next < entries.length) {
            const entry = entries[next++]!;
            try {
                if (entry.models !== undefined && entry.models.length > 0) {
                    // Each id is its own cache entry (several can share a file).
                    await Promise.all(entry.models.map((model) => fetchers.model(model)));
                } else {
                    await fetchers.file(entry.path);
                }
            } catch (error) {
                logger.warn(`could not precache ${entry.path}; it loads on first use:`, error);
            }
            onProgress?.(++done);
        }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, entries.length) }, worker));
}

/**
 * Precaching from start to finish (#285): the core tier, counted file by
 * file while the player waits to enter, then the run tier in the
 * background (#355), two at a time and uncounted - only that it started and
 * finished is reported. Nothing waits on the run tier: a file it hasn't
 * reached loads on first use.
 */
export async function runAssetPrecache(
    loadManifest: () => Promise<AssetManifest | null>,
    fetchers: AssetPrecacheFetchers,
    report: (state: AssetPrecacheState) => void,
): Promise<void> {
    const manifest = await loadManifest();
    if (manifest !== null) {
        const core = selectPrecacheEntries(manifest, 'core');
        report({ phase: 'core', done: 0, total: core.length });
        await precacheEntries(core, fetchers, (done) => report({ phase: 'core', done, total: core.length }), ENTRY_PRECACHE_CONCURRENCY);

        report({ phase: 'run', done: 0, total: 0 });
        await precacheEntries(selectPrecacheEntries(manifest, 'run'), fetchers, null, BACKGROUND_PRECACHE_CONCURRENCY);
    }
    report({ phase: 'done', done: 0, total: 0 });
}

/**
 * The game's own fetchers, used inside the precache worker: models into the
 * engine's model cache, checked without reading their bytes back (#355),
 * and other files by versioned URL into the HTTP cache.
 */
export const GAME_PRECACHE_FETCHERS: AssetPrecacheFetchers = {
    model: (model) => cacheCatalogedModel(model.kind, model.id),
    file: async (path) => {
        const response = await fetch(versionedAssetUrl(path));
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        // Read through, so the response is stored whole; the bytes themselves aren't kept here.
        await response.arrayBuffer();
    },
};

/** Starts the precaching somewhere (the precache worker, in the app) and hands back each state it reports. */
export type AssetPrecacheLauncher = (report: (state: AssetPrecacheState) => void) => void;

/**
 * Precaching for the page (#285): once per page session, on entering
 * Sorrem, the core tier (entry waits for it), then the run tier in the
 * background. The work itself runs off the page (see `launch`); this only
 * keeps the state it reports. A warm visit finds everything cached and is
 * through in moments; a later "Returning home" finds it done.
 */
class AssetPrecache {
    private state: AssetPrecacheState = { phase: 'idle', done: 0, total: 0 };
    private readonly listeners = new Set<() => void>();
    private started = false;

    readonly subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };

    readonly getState = (): AssetPrecacheState => this.state;

    /** Starts precaching with `launch`, once; later calls do nothing. */
    start(launch: AssetPrecacheLauncher): void {
        if (this.started) {
            return;
        }
        this.started = true;
        launch((state) => this.set(state));
    }

    private set(state: AssetPrecacheState): void {
        this.state = state;
        for (const listener of this.listeners) {
            listener();
        }
    }
}

let precache: AssetPrecache | null = null;

/** The page's precaching, made on first use. */
export function getAssetPrecache(): AssetPrecache {
    precache ??= new AssetPrecache();
    return precache;
}
